'use client';
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useSessionGuardStore } from '@/stores/sessionGuardStore';
import { useAuthStore } from '@/stores/authStore';
import { IDLE_TIMEOUT_MS, IDLE_WARNING_LEAD_MS } from '@/lib/constants';
import { isIdleLogoutExemptPath } from './idleExemptPaths';

export function useIdleTimer(): void {
  const pathname = usePathname();
  useEffect(() => {
    // Admin pages are exempt from the inactivity auto-logout — operators watch
    // live dashboards without input and must not be kicked to login mid-watch.
    // The effect re-runs on navigation, so leaving an admin route re-arms the
    // timer (and vice versa). Clear any pending warning when entering one.
    if (isIdleLogoutExemptPath(pathname)) {
      useSessionGuardStore.getState().actions.dismissIdleWarning();
      return;
    }

    let warningTimer: ReturnType<typeof setTimeout> | null = null;
    let logoutTimer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (warningTimer) clearTimeout(warningTimer);
      if (logoutTimer) clearTimeout(logoutTimer);
      warningTimer = null;
      logoutTimer = null;
    };

    const schedule = () => {
      cleanup();
      if (useAuthStore.getState().status !== 'authenticated') return;
      warningTimer = setTimeout(() => {
        useSessionGuardStore.getState().actions.showIdleWarning();
        logoutTimer = setTimeout(() => {
          void useAuthStore.getState().actions.forceLogout('idle');
        }, IDLE_WARNING_LEAD_MS);
      }, IDLE_TIMEOUT_MS);
    };

    const unsub = useSessionGuardStore.subscribe(
      (s) => s.lastActivityAt,
      () => schedule(),
    );
    schedule();

    return () => {
      cleanup();
      unsub();
    };
  }, [pathname]);
}
